/**
 * SoundManager - synthesized + sample-based sound effects.
 *
 * Why hybrid: we ship with synthesized fallbacks for every effect (a hoot for
 * win, a thump for death, a tick for pickups, an ascending arpeggio for level
 * complete) so the game is never silent. If real sample files are present at
 * the URLs in SOUND_REGISTRY, they take priority.
 *
 * Mute state is persisted via the `settings.soundEnabled` slot reserved in
 * step 8's save schema. Defaults to enabled.
 *
 * Web Audio nodes are created on demand and the AudioContext is resumed on
 * first user gesture so we play nicely with mobile autoplay policies.
 */

import Phaser from 'phaser';
import { load as loadSave, commit as commitSave } from '../persistence/saveState';
import { ParentBridge, type Mutable } from '../bridge/ParentBridge';

interface SampleSpec {
  key: string;
  url: string;
}

const SAMPLE_REGISTRY: SampleSpec[] = [
  { key: 'snd_hoot',        url: 'assets/snd_hoot.mp3' },
  { key: 'snd_splat',       url: 'assets/snd_splat.mp3' },
  { key: 'snd_coconut',     url: 'assets/snd_coconut.mp3' },
  { key: 'snd_level_clear', url: 'assets/snd_level_clear.mp3' },
];

class _SoundManager implements Mutable {
  private ctx?: AudioContext;
  private masterGain?: GainNode;
  private scene?: Phaser.Scene;
  private availableSamples = new Set<string>();
  private failedSamples = new Set<string>();
  private warned = false;
  private _enabled = true;

  /** Read persisted mute state on first call. Subsequent gets are cached. */
  private loaded = false;
  private ensureLoaded(): void {
    if (this.loaded) return;
    const save = loadSave();
    // settings.soundEnabled may be undefined (legacy save); default true.
    this._enabled = save.settings.soundEnabled !== false;
    this.loaded = true;
  }

  get enabled(): boolean {
    this.ensureLoaded();
    return this._enabled;
  }

  setEnabled(v: boolean): void {
    this.ensureLoaded();
    const changed = this._enabled !== v;
    this._enabled = v;
    const save = loadSave();
    save.settings.soundEnabled = v;
    commitSave(save);
    // Confirm to the parent (mute = !enabled). Only on a real change so a
    // redundant set produces no echo.
    if (changed) ParentBridge.notifyMuteChanged(!v);
  }

  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  // --- Mutable (ParentBridge) -------------------------------------------

  /** Parent command: muted=true means sound off. Idempotent. */
  applyMuteFromParent(muted: boolean): void {
    // setEnabled is idempotent on the persisted value, but we still want the
    // change to take effect; the bridge suppresses the echo while applying.
    this.setEnabled(!muted);
  }

  isMuted(): boolean {
    return !this.enabled;
  }

  /** Called by PreloadScene. Queues sample files; errors are non-fatal. */
  preloadInto(scene: Phaser.Scene): void {
    for (const s of SAMPLE_REGISTRY) {
      scene.load.audio(s.key, s.url);
    }
    scene.load.on(Phaser.Loader.Events.FILE_COMPLETE, (key: string) => {
      if (SAMPLE_REGISTRY.some((s) => s.key === key)) this.availableSamples.add(key);
    });
    scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      if (SAMPLE_REGISTRY.some((s) => s.key === file.key)) this.failedSamples.add(file.key);
    });
  }

  reportOnce(): void {
    if (this.warned) return;
    this.warned = true;
    if (this.failedSamples.size > 0) {
      console.info(
        `splat-chimp: synthesizing sounds for missing samples: ${Array.from(this.failedSamples).join(', ')}`
      );
    }
  }

  /**
   * Set the active scene reference so the manager knows where to play samples
   * through. Called from Menu and Game scenes' create().
   */
  setScene(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  // --- Public play methods ----------------------------------------------

  playCoconut(): void {
    if (!this.enabled) return;
    if (this.tryPlaySample('snd_coconut', 0.5)) return;
    this.synthBlip(880, 0.07, 'sine', 0.18);
  }

  playInvisibleCoconut(): void {
    if (!this.enabled) return;
    if (this.tryPlaySample('snd_coconut', 0.7)) return;
    // Brighter arpeggio for hidden coconut find
    this.synthBlip(660, 0.06, 'square', 0.14);
    setTimeout(() => this.synthBlip(990, 0.08, 'square', 0.16), 60);
    setTimeout(() => this.synthBlip(1320, 0.10, 'square', 0.18), 130);
  }

  playSplat(): void {
    if (!this.enabled) return;
    if (this.tryPlaySample('snd_splat', 0.7)) return;
    // Low thump that drops in pitch
    const ctx = this.audioCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(220, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.3);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.28, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(this.master(ctx));
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  }

  playWin(): void {
    if (!this.enabled) return;
    if (this.tryPlaySample('snd_hoot', 0.7)) return;
    // "Hoot" — quick rise then fall, vibrato-ish
    const notes = [523, 659, 784, 988]; // C5 E5 G5 B5
    notes.forEach((f, i) => {
      setTimeout(() => this.synthBlip(f, 0.12, 'triangle', 0.2), i * 110);
    });
  }

  playLevelClear(): void {
    if (!this.enabled) return;
    if (this.tryPlaySample('snd_level_clear', 0.65)) return;
    // Quick ascending arpeggio
    const notes = [440, 554, 659, 880]; // A4 C#5 E5 A5
    notes.forEach((f, i) => {
      setTimeout(() => this.synthBlip(f, 0.1, 'sine', 0.18), i * 70);
    });
  }

  /**
   * Race-start countdown beep. Two flavors:
   *   - 'ready' = short low beep (the lead-up clicks)
   *   - 'go'    = longer, higher beep (sounds on "GO!")
   *
   * Tones picked to evoke a real race starter: pure sine, A4 (440Hz) for the
   * ready beeps and A5 (880Hz, one octave up) for the GO beep. The GO beep
   * is also longer and louder so the player feels the lift-off moment.
   *
   * The scene is responsible for timing the calls — see GameScene's
   * scheduleRaceCountdown(). No sample fallback: this is intentionally
   * synthesized so it always plays in lockstep with the visual GO! banner
   * with zero load delay.
   */
  playRaceBeep(kind: 'ready' | 'go'): void {
    if (!this.enabled) return;
    if (kind === 'ready') {
      this.synthBlip(440, 0.12, 'sine', 0.16);
    } else {
      this.synthBlip(880, 0.28, 'sine', 0.22);
    }
  }

  // --- Internals --------------------------------------------------------

  private audioCtx(): AudioContext | undefined {
    if (this.ctx) {
      // Resume in case it was suspended by browser autoplay policy.
      if (this.ctx.state === 'suspended') {
        void this.ctx.resume();
      }
      return this.ctx;
    }
    try {
      const AC: typeof AudioContext =
        window.AudioContext ||
        // @ts-expect-error - webkit prefix on older Safari
        window.webkitAudioContext;
      if (!AC) return undefined;
      this.ctx = new AC();
      return this.ctx;
    } catch (err) {
      console.warn('splat-chimp: AudioContext unavailable', err);
      return undefined;
    }
  }

  private master(ctx: AudioContext): GainNode {
    if (!this.masterGain) {
      this.masterGain = ctx.createGain();
      this.masterGain.gain.value = 0.7;
      this.masterGain.connect(ctx.destination);
    }
    return this.masterGain;
  }

  private synthBlip(freq: number, durSec: number, type: OscillatorType, vol: number): void {
    const ctx = this.audioCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durSec);
    osc.connect(gain).connect(this.master(ctx));
    osc.start();
    osc.stop(ctx.currentTime + durSec + 0.02);
  }

  private tryPlaySample(key: string, volume: number): boolean {
    if (!this.scene || !this.availableSamples.has(key)) return false;
    try {
      this.scene.sound.play(key, { volume });
      return true;
    } catch (err) {
      console.warn(`splat-chimp: sample ${key} failed to play`, err);
      return false;
    }
  }
}

export const SoundManager = new _SoundManager();

// Register with the parent bridge so SET_MUTE commands route here.
ParentBridge.registerMutable(SoundManager);
